import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { FOI_STATE_LABELS } from '../../lib/constants';
import type { FoiRecord, FoiState } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STEPS: FoiState[] = ['RECEIVED', 'ASSIGNED', 'RESPONDED'];

function stepDate(foi: FoiRecord, step: FoiState): string | null | undefined {
  if (step === 'RECEIVED') return foi.createdAt;
  if (step === 'ASSIGNED') return foi.assignedAt;
  if (step === 'RESPONDED') return foi.respondedAt;
  return undefined;
}

export function FoiStatus() {
  const { ref = '' } = useParams();
  const q = useApiQuery<FoiRecord>(`/api/v1/opengov/foi/${encodeURIComponent(ref)}`);
  const [grounds, setGrounds] = useState('');
  const [appealBusy, setAppealBusy] = useState(false);
  const [appealError, setAppealError] = useState<string | null>(null);
  const [appealSent, setAppealSent] = useState(false);

  const submitAppeal = async () => {
    setAppealBusy(true);
    setAppealError(null);
    try {
      await api.post(`/api/v1/opengov/foi/${encodeURIComponent(ref)}/appeal`, { grounds });
      setAppealSent(true);
      q.reload();
    } catch (e) {
      setAppealError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit appeal');
    } finally {
      setAppealBusy(false);
    }
  };

  return (
    <div>
      <Link to="/foi" className="mb-4 inline-block text-sm font-semibold text-brand-600 hover:underline">
        ‹ Back to Freedom of Information
      </Link>

      {q.loading ? <Spinner /> : null}
      {q.error ? (
        <ErrorNote message={`${q.error} — check the reference number and try again.`} />
      ) : null}

      {q.data ? (
        <>
          <PageHeader
            title={q.data.refNo}
            subtitle={q.data.subject}
            actions={<StatusBadge status={q.data.state} />}
          />

          {q.data.overdue ? (
            <div className="mb-6">
              <ErrorNote message="This request is past the statutory 21-day response window under the Access to Information Act 2018." />
            </div>
          ) : null}

          <div className="mb-6 card p-5">
            <ol className="grid gap-4 sm:grid-cols-3">
              {STEPS.map((step, i) => {
                const date = stepDate(q.data!, step);
                const reached = Boolean(date);
                return (
                  <li key={step} className="relative">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                          reached ? 'bg-brand-600 text-white' : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className={`text-sm font-semibold ${reached ? 'text-slate-900' : 'text-slate-400'}`}>
                        {FOI_STATE_LABELS[step]}
                      </span>
                    </div>
                    <div className="ml-8 mt-0.5 text-xs text-slate-400">{date ? formatDateTime(date) : 'Pending'}</div>
                  </li>
                );
              })}
            </ol>
            {q.data.state === 'APPEALED' ? (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <div className="text-sm font-semibold text-amber-700">Under appeal since {formatDateTime(q.data.appealedAt)}</div>
                {q.data.appealGrounds ? <p className="mt-1 text-sm text-slate-600">{q.data.appealGrounds}</p> : null}
              </div>
            ) : null}
          </div>

          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target ministry / department</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{q.data.targetMda}</div>
            </div>
            <div className="card px-4 py-3.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Statutory due date</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{formatDate(q.data.statutoryDueAt)}</div>
            </div>
          </div>

          {q.data.responseSummary ? (
            <div className="mb-6">
              <h2 className="mb-2 text-sm font-bold text-slate-700">Response</h2>
              <div className="card p-4">
                <p className="text-sm text-slate-700">{q.data.responseSummary}</p>
                {q.data.responseDocIds.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {q.data.responseDocIds.map((id) => (
                      <span key={id} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-500 ring-1 ring-slate-200">
                        {id}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {q.data.state === 'RESPONDED' ? (
            <div className="card p-5">
              <h2 className="mb-1 text-base font-bold text-slate-900">Not satisfied with the response?</h2>
              <p className="mb-3 text-sm text-slate-500">
                You may appeal this response, explaining the grounds for your appeal.
              </p>
              {appealSent ? (
                <p className="text-sm font-semibold text-emerald-700">Your appeal has been recorded.</p>
              ) : (
                <div className="space-y-3">
                  {appealError ? <ErrorNote message={appealError} /> : null}
                  <Field label="Grounds for appeal">
                    <textarea
                      className="field-input"
                      rows={3}
                      value={grounds}
                      onChange={(e) => setGrounds(e.target.value)}
                      placeholder="Explain why you believe the response was inadequate or incorrect."
                    />
                  </Field>
                  <button className="btn-primary" disabled={appealBusy || grounds.trim().length < 10} onClick={submitAppeal}>
                    {appealBusy ? 'Submitting…' : 'Submit appeal'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
